import React, { useState, useMemo } from "react";
import { Box, Typography, IconButton, Alert, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Paperclip,
  Image as ImageIcon,
  Video,
  Send,
  MessageSquare,
  AlertTriangle,
  X,
} from "lucide-react";

import { CustomTextArea } from "../../components/CustomTextArea";
import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { AttachmentUploader, validateAttachmentFile } from "../../components/AttachmentUploader";
import { FileDropOverlay } from "../../components/FileDropOverlay";
import {
  CustomMentionsInput,
  getRoleDisplayName,
  createMentionUsers,
  hasValidContent,
} from "../../components/CustomMentionsInput";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { usePasteImage } from "../../components/hooks/usePasteImage";
import { useFileDrop } from "../../components/hooks/useFileDrop";

export function ThreadComposer({
  onPost,
  placeholder = "Escribe algo...",
  clientData = null,
  currentUser = null,
  disabled = false,
  disabledMessage = null,
  disabledPlaceholder = null,
  hideTextInput = false,
  consultants = [],
  allUsers = [],
  conversationFontSize = null, // Tamaño de letra personalizado en píxeles
}) {
  const theme = useTheme();
  const { colors } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isImportant, setIsImportant] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Helper para obtener el tamaño de letra a usar
  const getMessageFontSize = () => {
    if (conversationFontSize && typeof conversationFontSize === 'number') {
      const safeSize = Math.min(conversationFontSize, 16);
      return `${safeSize}px`;
    }
    return "0.85rem"; // Fallback al tamaño por defecto
  };

  const messageFontSize = getMessageFontSize();

  // Función para manejar el toggle de importante
  const handleToggleImportant = () => {
    setIsImportant(!isImportant);
  };

  // Crear lista de usuarios para menciones
  const mentionUsers = useMemo(() => {
    // Usar helper reutilizable (incluye profileImage si existe)
    return createMentionUsers(consultants, allUsers, clientData, currentUser);
  }, [consultants, allUsers, clientData, currentUser]);

  const handlePost = async () => {
    if (disabled || (!hasValidContent(content) && attachments.length === 0))
      return;

    // Determinar el tipo de mensaje basado en los estados
    const finalMessageType = isImportant ? "important" : "normal";

    setIsSubmitting(true);
    setError(null);

    try {
      await onPost(content, finalMessageType, null, attachments);

      // Limpiar formulario después del envío exitoso
      setContent("");
      setAttachments([]);
      setIsImportant(false);
    } catch (error) {
      setError(error.message || "Error al enviar el mensaje");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!isSubmitting) {
        handlePost();
      }
    }
  };

  // Hook para detectar imágenes pegadas desde el portapapeles
  const handlePasteImage = usePasteImage(setAttachments, {
    onError: setError,
    disabled,
  });

  const addDroppedFiles = (files) => {
    const validationError = files.map(validateAttachmentFile).find(Boolean);
    if (validationError) {
      setError(validationError);
      return;
    }
    setAttachments((prev) => [
      ...prev,
      ...files.filter(
        (file) => !prev.some((f) => f.name === file.name && f.size === file.size)
      ),
    ]);
  };

  const { isDragging, dropHandlers } = useFileDrop({
    onFiles: addDroppedFiles,
    disabled: disabled || isSubmitting,
  });

  return (
    <Box
      {...dropHandlers}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => !disabled && setHovered(false)}
      sx={{
        border: `1px solid ${disabled
          ? theme.palette.action.disabled
          : focused
            ? colors.primary.main
            : hovered
              ? theme.palette.border?.default || theme.palette.divider
              : theme.palette.border?.input || theme.palette.divider
          }`,
        borderRadius: "8px",
        padding: "12px",
        marginBottom: "16px",
        backgroundColor: disabled
          ? theme.palette.action.hover
          : theme.palette.background.paper,
        opacity: disabled ? 0.7 : 1,
        position: "relative",
        transition: "all 0.2s ease",
        ...(focused && !disabled
          ? {
            boxShadow: `0 0 0 2px ${colors.primary.main}22`,
          }
          : {}),
      }}
    >
      {isDragging && <FileDropOverlay inset="4px" />}

      {/* Mensaje de estado deshabilitado */}
      {disabled && disabledMessage && (
        <AlertNote variant="warning" sx={{ marginBottom: "12px" }}>
          {disabledMessage}
        </AlertNote>
      )}

      {!hideTextInput && (
        <CustomMentionsInput
          placeholder={
            disabled
              ? (disabledPlaceholder ?? "No puedes participar en esta conversación")
              : placeholder
          }
          value={content}
          onChange={(event, newValue, newPlainTextValue, mentions) => {
            if (!disabled) setContent(newValue);
          }}
          onKeyDown={disabled ? undefined : handleKeyDown}
          onFocus={() => !disabled && setFocused(true)}
          onBlur={() => !disabled && setFocused(false)}
          onPaste={handlePasteImage}
          disabled={disabled}
          users={mentionUsers}
          variant="composer"
          suggestionsPortal={true}
          suggestionsZIndex={1800}
          sx={{
            minHeight: "50px",
            fontSize: messageFontSize,
            lineHeight: 1.4,
            maxHeight: "200px",
            width: "100%",
            "&:focus": {
              outline: "none",
              boxShadow: "none",
            },
            "&::placeholder": {
              color: theme.palette.text.secondary,
            },
            "&:disabled": {
              backgroundColor: "transparent",
              color: theme.palette.text.disabled,
              cursor: "not-allowed",
            },
          }}
        />
      )}

      {/* Mostrar archivos adjuntos si hay alguno */}
      {attachments.length > 0 && (
        <Box sx={{ marginTop: "8px", marginBottom: "6px" }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
              marginBottom: "6px",
              display: "block",
            }}
          >
            Archivos adjuntos ({attachments.length}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {attachments.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 8px",
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: "4px",
                  border: `1px solid ${theme.palette.divider}`,
                  maxWidth: "200px",
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, flexShrink: 0 }}>
                  {file.type.startsWith("image/") ? (
                    <ImageIcon size={14} />
                  ) : file.type.startsWith("video/") ? (
                    <Video size={14} />
                  ) : (
                    <Paperclip size={14} />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  title={file.name}
                  sx={{
                    fontSize: "0.75rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    setAttachments(attachments.filter((_, i) => i !== index))
                  }
                  sx={{
                    padding: "2px",
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <X size={12} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Barra de herramientas */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "6px",
          paddingTop: "6px",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Stack horizontal: archivos + separador + iconos de acciones */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Componente de adjuntos - Movido aquí */}
          <Box sx={{ mr: 1 }}>
            <Tooltip title="Adjuntar archivo" arrow placement="bottom">
              <Box>
                <AttachmentUploader
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  disabled={disabled || isSubmitting}
                  compact={true} // Modo compacto para la nueva ubicación
                />
              </Box>
            </Tooltip>
          </Box>

          {/* Línea vertical separadora - Más visible */}
          <Box
            sx={{
              width: "1px", // Aumentado de 1px para más visibilidad
              height: "24px",
              backgroundColor: theme.palette.divider, // Cambiado a divider para mejor contraste
              borderRadius: "1px", // Bordes redondeados para mejor apariencia
              mx: 1,
            }}
          />

          {/* Icono de marcar como importante */}
          <Tooltip title="Marcar como importante" arrow placement="bottom">
            <IconButton
              size="small"
              onClick={() => !disabled && handleToggleImportant()}
              disabled={disabled}
              sx={{
                color: theme.palette.text.secondary,
                backgroundColor: isImportant ? "#fef3c7" : "transparent",
                "&:hover": {
                  backgroundColor: isImportant
                    ? "#fde68a"
                    : "rgba(249, 115, 22, 0.1)",
                },
                "&:disabled": {
                  color: theme.palette.text.disabled,
                },
              }}
            >
              <AlertTriangle size={16} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Botón de compartir */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!disabled && !isMobile && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
              }}
            >
              Ctrl + Enter para enviar
            </Typography>
          )}

          <Button
            onClick={handlePost}
            size={isMobile ? "icon" : "default"}
            aria-label="Compartir"
            disabled={
              disabled ||
              (!hasValidContent(content) && attachments.length === 0) ||
              isSubmitting
            }
          >
            {!isMobile && (isSubmitting ? "Enviando..." : "Compartir")}
            <Send size={16} />
          </Button>
        </Box>
      </Box>

      {/* Mostrar errores */}
      {error && (
        <Alert
          severity="error"
          sx={{ marginTop: "12px" }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}
